import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, clearSession, SESSION_MAX_AGE_MS } from '../services/api';

const AuthContext = createContext(null);

function isSessionExpired() {
  const loginAt = Number(localStorage.getItem('login_at'));
  return !loginAt || (Date.now() - loginAt) > SESSION_MAX_AGE_MS;
}

const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000; // 2 hours

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);
  const idleTimerRef = useRef(null);

  const logout = useCallback(async () => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    await authAPI.logout().catch(err => console.error('[logout] request failed:', err));
    clearSession();
    setUser(null);
  }, []);

  // local session clear ONLY — does not call the logout
  // endpoint, so no logout timestamp is recorded.
  const forceIdleLogout = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    clearSession();
    setUser(null);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(forceIdleLogout, IDLE_LIMIT_MS);
  }, [forceIdleLogout]);

  const scheduleAutoLogout = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    const loginAt = Number(localStorage.getItem('login_at'));
    if (!loginAt) return;
    const remaining = SESSION_MAX_AGE_MS - (Date.now() - loginAt);
    if (remaining <= 0) { logout(); return; }
    logoutTimerRef.current = setTimeout(logout, remaining);
  }, [logout]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      if (isSessionExpired()) {
        clearSession();
      } else {
        setUser(JSON.parse(storedUser));
        scheduleAutoLogout();
      }
    }
    setLoading(false);
    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    localStorage.setItem('token', data.token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('login_at', String(Date.now()));
    setUser(data.user);
    scheduleAutoLogout();
    return data;
  };

  const register = async (formData) => {
    const { data } = await authAPI.register(formData);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);